import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useBrokerPayments, useBrokers } from "services/hooks/rentals";
import { currencyFormatter } from "utilities/currencyFormatter";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return currencyFormatter(num);
};

const landlordNameFromRow = (row) => {
  const landlord = row.related_broker_landlord?.related_landlord;
  if (!landlord) return row.landlord_name || "—";
  return (
    landlord.business_name ||
    `${landlord.related_user?.first_name || ""} ${landlord.related_user?.last_name || ""}`.trim() ||
    "—"
  );
};

const BrokerCommissions = () => {
  const role = useSelector((state) => state.role);
  const brokerFromState = useSelector((state) => state.broker);
  const isBroker = role === "broker";
  const isStaff = role === "admin" || role === "manager";

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [brokerFilterId, setBrokerFilterId] = useState("");
  const [paidFilter, setPaidFilter] = useState("");

  const { brokers, isLoadingBrokers } = useBrokers();
  const {
    brokerPayments,
    isLoadingBrokerPayments,
    errorBrokerPayments,
  } = useBrokerPayments({
    brokerId: isBroker ? undefined : brokerFilterId ? Number(brokerFilterId) : undefined,
    month: month ? Number(month) : undefined,
    year: year ? Number(year) : undefined,
    commissionPaid: paidFilter === "" ? undefined : paidFilter === "true",
  });

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [current - 1, current, current + 1];
  }, []);

  if (!isBroker && !isStaff) {
    return (
      <Box m={2}>
        <Alert severity="warning">You do not have access to broker commissions.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
        {isBroker ? "My Commissions" : "Broker Commissions"}
      </Typography>
      {isBroker && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {brokerFromState?.broker_name
            ? `Broker: ${brokerFromState.broker_name} — track commission amounts and payment status.`
            : "Your commission records and payment status."}
        </Typography>
      )}

      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
            <MenuItem value="">All months</MenuItem>
            {MONTHS.map((m) => (
              <MenuItem key={m.value} value={String(m.value)}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select label="Year" value={year} onChange={(e) => setYear(e.target.value)}>
            <MenuItem value="">All years</MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isStaff && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Broker</InputLabel>
            <Select label="Broker" value={brokerFilterId} onChange={(e) => setBrokerFilterId(e.target.value)}>
              <MenuItem value="">All brokers</MenuItem>
              {(brokers || []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.broker_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="false">Unpaid</MenuItem>
            <MenuItem value="true">Paid</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {errorBrokerPayments && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorBrokerPayments}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingBrokerPayments || (isStaff && isLoadingBrokers) ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {isStaff && <TableCell>Broker</TableCell>}
                <TableCell>Landlord</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Base</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date paid</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brokerPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isStaff ? 7 : 6}>No commission records for this period.</TableCell>
                </TableRow>
              ) : (
                brokerPayments.map((row) => (
                  <TableRow key={row.id}>
                    {isStaff && (
                      <TableCell>
                        {row.related_broker_landlord?.related_broker?.broker_name || "—"}
                      </TableCell>
                    )}
                    <TableCell>{landlordNameFromRow(row)}</TableCell>
                    <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                    <TableCell>
                      {(row.commission_base || row.related_broker_landlord?.commission_base || "gross").toUpperCase()}
                    </TableCell>
                    <TableCell>
                      {row.commission_paid ? (
                        <Chip size="small" label="Paid" color="success" variant="outlined" />
                      ) : (
                        <Chip size="small" label="Unpaid" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{row.date_paid || "—"}</TableCell>
                    <TableCell>{row.description || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default BrokerCommissions;
